import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { activeUserAtom } from "../../../atoms/user/activeUserAtom";
import { useMediaQueryBreakpoint } from "../../../hooks/useMediaQuery";
import useModal from "../../../hooks/useModal";
import { useReleaseTrackDragDrop } from "../../../hooks/useReleaseTrackDragDrop";
import { ReleaseTrack } from "../../../store/models/release";
import { FeedbackModal } from "../FeedbackModal/FeedbackModal";
import { EditReleaseTrackSidePanel } from "../ReleaseTrack/EditReleaseTrackSidePanel/EditReleaseTrackSidePanel";
import {
  releaseTrackColumnOrderArtist,
  releaseTrackColumnOrderConsumer,
  releaseTrackColumns,
  ReleaseTrackTableRowId,
} from "./defaultColumnsForReleaseTrackTable";
import { DraggableArtistReleaseTrackTableRow } from "./DraggableArtistReleaseTrackTableRow";

export enum ReleaseTrackTableVariant {
  CONSUMER = "consumer", // Table for users who purchased releases
  EDIT = "edit", // Table for artists editing their releases (Release Details Page)
  CREATE = "create", // Table for artists creating new releases (Create Release Flow)
}

interface ReleaseTrackTableProps {
  releaseTracks: ReleaseTrack[];
  releaseId?: number;
  artistUserId?: number;
  unitPrice?: string | null;
  lowestUnitPriceProductId?: number;
  selectedProductId?: number;
  variant?: ReleaseTrackTableVariant;
  releaseIsStreamEnabled: boolean;
  currency?: string;
  isArtistOnRelease?: boolean;
  productToGetPriceFromId?: number;
}

export const ArtistReleaseTrackTable = ({
  releaseTracks,
  releaseId,
  artistUserId,
  currency,
  unitPrice,
  selectedProductId,
  lowestUnitPriceProductId,
  variant = ReleaseTrackTableVariant.CONSUMER,
  releaseIsStreamEnabled,
}: ReleaseTrackTableProps) => {
  const activeUser = useAtomValue(activeUserAtom);
  const {
    isOpen: isFeedbackModalOpen,
    openModal: openFeedbackModal,
    closeModal: closeFeedbackModal,
  } = useModal();
  const isConsumerMode = variant === ReleaseTrackTableVariant.CONSUMER;
  const isCreateMode = variant === ReleaseTrackTableVariant.CREATE;
  const { isDesktop } = useMediaQueryBreakpoint();
  const { handleDragStart, handleDragEnd, isPending } = useReleaseTrackDragDrop(
    {
      releaseTracks,
    },
  );

  const isArtistOnRelease = useMemo(() => {
    return activeUser?.id === artistUserId;
  }, [activeUser, artistUserId]);

  const [columnVisibility, columnOrder] = useMemo(() => {
    const isArtistMode = isArtistOnRelease && !isConsumerMode;

    const columns = {
      [ReleaseTrackTableRowId.ReorderTab]: isArtistMode,
      [ReleaseTrackTableRowId.Title]: true,
      [ReleaseTrackTableRowId.ArtistTitle]: isArtistMode && isDesktop,
      [ReleaseTrackTableRowId.Price]: !isCreateMode && isDesktop,
      [ReleaseTrackTableRowId.PlaySection]: true,
      [ReleaseTrackTableRowId.Status]: isArtistMode && isDesktop,
      [ReleaseTrackTableRowId.CartButton]: !isArtistMode,
      [ReleaseTrackTableRowId.DropDown]: !isArtistMode && isDesktop,
      [ReleaseTrackTableRowId.ActionButton]: isArtistMode,
    };

    return [
      columns,
      isArtistMode
        ? releaseTrackColumnOrderArtist
        : releaseTrackColumnOrderConsumer,
    ];
  }, [isArtistOnRelease, isDesktop, isCreateMode, isConsumerMode]);
  const memoizedCol = useMemo(
    () => releaseTrackColumns(releaseId),
    [releaseId],
  );
  const table = useReactTable({
    data: releaseTracks,
    columns: memoizedCol,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
    state: {
      columnOrder,
      columnVisibility,
    },
    meta: {
      reorderLoading: isPending,
      handlePlayMusic: () => {},
      isFooterPlaying: false,
    },
  });

  const dataIds = useMemo<UniqueIdentifier[]>(
    () => releaseTracks?.map(({ id }) => id),
    [releaseTracks],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  return (
    <>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} sx={{ flex: 1 }}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableCell
                      key={header.id}
                      sx={(theme) => ({
                        flex: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        fontWeight: 600,
                        fontSize: "1rem",
                        ...(header.column.id !==
                          ReleaseTrackTableRowId.Title && {
                          maxWidth: header.column.getSize(),
                          width: header.column.getSize(),
                        }),
                        ...(header.column.id ===
                          ReleaseTrackTableRowId.ReorderTab && {
                          padding: 0,
                        }),
                        // PlaySection doesn't need that much padding on mobile
                        ...(header.column.id ===
                          ReleaseTrackTableRowId.PlaySection &&
                          !isDesktop && {
                            padding: 0,
                          }),
                      })}
                    >
                      {flexRender(header.column.columnDef.header, {
                        ...header.getContext(),
                        isDesktop,
                      })}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            <SortableContext
              items={dataIds}
              strategy={verticalListSortingStrategy}
            >
              {table.getRowModel().rows.map((row) => {
                return (
                  <DraggableArtistReleaseTrackTableRow
                    key={row.id}
                    row={row}
                    unitPrice={unitPrice}
                    currency={currency}
                    isArtistOnRelease={isArtistOnRelease}
                    selectedProductId={selectedProductId}
                    openFeedbackModal={openFeedbackModal}
                    lowestUnitPriceProductId={lowestUnitPriceProductId}
                    isCreateMode={isCreateMode}
                    isDesktop={isDesktop}
                    releaseIsStreamEnabled={releaseIsStreamEnabled}
                  />
                );
              })}
            </SortableContext>
          </TableBody>
        </Table>
      </DndContext>
      {isFeedbackModalOpen && <FeedbackModal onClose={closeFeedbackModal} />}
      <EditReleaseTrackSidePanel />
    </>
  );
};
